import { format } from 'date-fns'
import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { generatePath, useNavigate } from 'react-router'

import type { ParentNoticeRes } from '~/@common/api/parentApi'
import { routeName } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import Table from '~/@common/ui/Table/Table'

import { ParentNoticeService } from './ParentNotice.service'
import { S } from './ParentNotice.style'

const NOTICE_TYPE_PREFIX: Record<ParentNoticeRes['type'], string> = {
  NOTICE: '[공지]',
  PLAN: '[일정]',
  NEWS: '[소식]',
} as const

type ViewProps = {
  notices: ParentNoticeRes[]
  onNoticeClick: (notice: ParentNoticeRes) => void
}

const TableView = ({ notices, onNoticeClick }: ViewProps) => {
  return (
    <S.NoticeTable>
      <Table.Header ratio="80px 1fr">
        <Table.HeaderItem>날짜</Table.HeaderItem>
        <Table.HeaderItem alignStart>공지사항</Table.HeaderItem>
      </Table.Header>
      <Table.Body>
        {notices.map((notice) => (
          <Table.Row key={notice.id} ratio="80px 1fr" onClick={() => onNoticeClick(notice)}>
            <Table.Item className="date-cell">
              {format(new Date(notice.createDatetime), 'yyyy.MM.dd')}
            </Table.Item>
            <Table.Item className="title-cell">
              <span className="title-text">
                <strong>{NOTICE_TYPE_PREFIX[notice.type] ?? ''}</strong> {notice.title}
              </span>
            </Table.Item>
          </Table.Row>
        ))}
      </Table.Body>
    </S.NoticeTable>
  )
}

const CardView = ({ notices, onNoticeClick }: ViewProps) => {
  return (
    <S.CardList>
      {notices.map((notice) => (
        <S.Card key={notice.id} onClick={() => onNoticeClick(notice)}>
          <S.CardMeta>{format(new Date(notice.createDatetime), 'yy.MM.dd')}</S.CardMeta>
          <S.CardTitle>
            <strong>{NOTICE_TYPE_PREFIX[notice.type] ?? ''}</strong> {notice.title}
          </S.CardTitle>
        </S.Card>
      ))}
    </S.CardList>
  )
}

// ─── 메인 페이지 컴포넌트 ───

const ParentNoticePage = observer(() => {
  const { isMobile } = useStudentAppMediaQuery()
  const navigate = useNavigate()
  const service = useRepository(ParentNoticeService)

  useEffect(() => {
    service.fetchNotices()
  }, [service])

  const handleNoticeClick = (notice: ParentNoticeRes) => {
    navigate(generatePath(routeName.parent.noticeDetail, { noticeId: String(notice.id) }))
  }

  if (service.isLoading) return null

  if (service.isEmpty) {
    return <S.Empty>등록된 공지사항이 없습니다.</S.Empty>
  }

  return (
    <S.Container>
      {isMobile ? (
        <CardView notices={service.notices} onNoticeClick={handleNoticeClick} />
      ) : (
        <TableView notices={service.notices} onNoticeClick={handleNoticeClick} />
      )}
    </S.Container>
  )
})

export default ParentNoticePage
